import type { ReactElement } from 'react';
import {
  IconCalendario,
  IconCirculo,
  IconCopiloto,
  IconDiario,
  IconFoco,
  IconHoy,
  IconMetas,
  IconPerfil,
  IconProgreso,
  IconRevision,
  IconSemana,
} from './iconos';

/**
 * LA NAVEGACIÓN, y sobre todo **qué significa cada sección**.
 *
 * Hasta aquí la app tenía cinco destinos en una barra inferior y, dentro de
 * Perfil, una pila de cinco botones fantasma idénticos —Diario, Revisión,
 * Calendario, Círculo, Copiloto— que no eran una sección: eran un cajón. Un
 * cajón es lo que aparece cuando nadie decidió dónde va cada cosa.
 *
 * Aquí se decide. Cada una de las cinco secciones tiene un sentido y unos hijos:
 *
 *   HOY       el presente. Lo que toca ahora.
 *             ← Diario (lo de hoy escrito), Calendario (el registro de los días),
 *               el detalle de un hábito, Semana (las tareas de los siete días).
 *   METAS     el rumbo. A dónde va esto y por qué.
 *             ← Áreas (las partes de tu vida de las que cuelga todo).
 *   FOCO      el instrumento. Una sola cosa durante un rato.
 *   PROGRESO  en qué te estás convirtiendo.
 *             ← Revisión (leer tu semana), Copiloto (que te la explique).
 *   PERFIL    quién eres y lo que ya está cerrado.
 *             ← Rewind, Círculo, Ajustes, Cuenta.
 *
 * Esa paternidad no es documentación: la usa `indiceActivo`, y es lo que hace
 * que al abrir el Diario la barra siga marcando **Hoy**. Antes no marcaba nada,
 * y una barra que se apaga cuando entras en una pantalla te dice que esa
 * pantalla no forma parte de la app.
 *
 * Dos expresiones del mismo árbol, y ninguna es la versión reducida de la otra:
 *   · MÓVIL      barra inferior de cinco, con la pieza acuñada que se desliza.
 *   · ESCRITORIO riel lateral con los cinco arriba, los secundarios debajo del
 *                filete y Ajustes al fondo. En escritorio hay sitio: esconder
 *                cosas ahí no es contención, es esconderlas.
 */

type Glifo = (p: { size?: number; className?: string }) => ReactElement;

type Principal = {
  readonly to: string;
  readonly label: string;
  readonly Icon: Glifo;
  /** Rutas que pertenecen a esta sección aunque no cuelguen de su ruta. */
  readonly hijos: readonly string[];
};

export const PRINCIPALES = [
  { to: '/', label: 'Hoy', Icon: IconHoy, hijos: ['/diario', '/calendario', '/habito', '/semana'] },
  { to: '/metas', label: 'Metas', Icon: IconMetas, hijos: ['/areas'] },
  { to: '/foco', label: 'Foco', Icon: IconFoco, hijos: [] },
  { to: '/progreso', label: 'Progreso', Icon: IconProgreso, hijos: ['/revision', '/copiloto'] },
  {
    to: '/perfil',
    label: 'Perfil',
    Icon: IconPerfil,
    hijos: ['/rewind', '/circulo', '/cuenta', '/ajustes', '/conexiones'],
  },
] as const satisfies readonly Principal[];

/**
 * Los secundarios, con la frase que dice para qué sirven.
 *
 * La frase no es adorno: en el riel se lee debajo del nombre, y es la diferencia
 * entre una lista de nombres —«Revisión», «Círculo»— y una lista de sitios a los
 * que se va por algo.
 */
export const SECUNDARIOS = [
  { to: '/semana', label: 'Semana', Icon: IconSemana, nota: 'Tus tareas, día a día' },
  { to: '/diario', label: 'Diario', Icon: IconDiario, nota: 'Lo de hoy, escrito' },
  { to: '/calendario', label: 'Calendario', Icon: IconCalendario, nota: 'Qué pasó, día a día' },
  { to: '/revision', label: 'Revisión', Icon: IconRevision, nota: 'Leer tu semana' },
  { to: '/copiloto', label: 'Copiloto', Icon: IconCopiloto, nota: 'Que te la explique' },
  { to: '/circulo', label: 'Círculo', Icon: IconCirculo, nota: 'Lo que decides enseñar' },
] as const;

export function casa(pathname: string, ruta: string): boolean {
  return pathname === ruta || pathname.startsWith(`${ruta}/`);
}

/**
 * Qué sección está activa. Devuelve siempre un índice válido —nunca −1— porque
 * la alternativa es una barra sin ningún destino marcado, y eso se lee como un
 * fallo de la app, no como «estás en otro sitio».
 */
export function indiceActivo(pathname: string): number {
  // `/` casa con todo si se comprueba por prefijo, así que Hoy va el último.
  const propio = PRINCIPALES.findIndex((d, i) => i > 0 && casa(pathname, d.to));
  if (propio >= 0) return propio;

  const heredado = PRINCIPALES.findIndex((d) => d.hijos.some((hijo) => casa(pathname, hijo)));
  if (heredado >= 0) return heredado;

  return 0;
}

/** Hacia dónde va el viaje entre dos rutas. */
export type Rumbo = 'adelante' | 'atras' | 'ninguno';

/**
 * EL SENTIDO DEL VIAJE, que es lo que convierte un cambio de pantalla en un
 * movimiento en vez de en un corte.
 *
 * Sale del **orden de la barra**, no del historial. Es la diferencia entre una
 * app que tiene sitios y una pila de tarjetas: si el rumbo lo marcara el
 * historial, ir de Perfil a Hoy se deslizaría igual que ir de Hoy a Perfil
 * —las dos son «adelante» en la pila— y la barra estaría diciendo una cosa
 * mientras la pantalla dice otra.
 *
 * Y manda la SECCIÓN, no la ruta. Abrir el Diario desde Hoy no es viajar: el
 * Diario **es** Hoy, y así lo dice `indiceActivo` cuando deja la barra marcando
 * Hoy. Deslizar ahí sería contradecir a la propia barra.
 *
 * Vive aquí, en el módulo que no pinta nada, porque es una regla y las reglas
 * se prueban.
 */
export function rumbo(desde: string, hacia: string): Rumbo {
  const a = indiceActivo(desde);
  const b = indiceActivo(hacia);
  if (a === b) return 'ninguno';
  return b > a ? 'adelante' : 'atras';
}

/**
 * Los tipos de la transición de pantalla que el router pide al navegador.
 *
 * SIN ORIGEN NO HAY TRANSICIÓN. Al arrancar no se viene de ninguna pantalla, y
 * la transición del primer pintado solo podía fotografiar el lienzo vacío. En el
 * iPhone instalado, tras «Actualizar», esa transición se quedaba a medias: la foto
 * vacía —negra— tapaba la app y solo asomaba la barra de abajo, que lleva su
 * propio nombre y se dibuja aparte (lo vio el cliente, 2026-10-30). `false` le
 * dice al router que navegue sin `startViewTransition`.
 */
export function tiposDeTransicion(desde: string | undefined, hacia: string): string[] | false {
  if (desde === undefined) return false;
  const sentido = rumbo(desde, hacia);
  return sentido === 'ninguno' ? [] : [sentido];
}
